import clsx from 'clsx'
import { groupBy } from 'lodash-es'
import { Fragment, useRef, useState } from 'react'

import { FvButton, Icon } from '@fv/client-components'

import { SearchForm, type SearchFormHandle } from '@/components/SearchForm'

import { ArticleDetails, ArticleDetailsQuery } from './ArticleDetails'
import { useNmfcArticleSearch, useNmfcDescriptionSearch } from './queries'
import type { NmfcSearchResponse, OnSelectArgs } from './types'
import { parseNmfc } from './utils'

type GroupHeadingProps = {
  name: string
}
const NmfcGroupHeading = ({ name }: GroupHeadingProps) => {
  return (
    <div className="mb-3">
      <Icon
        icon="dot-circle"
        className="text-fv-gray-dark font-semibold"
        fixedWidth
      />
      <span className="text-fv-gray-dark font-semibold">{name}</span>
    </div>
  )
}

export type NmfcSearchContentProps = {
  initialSearch?: string
  density?: number
  onSelect: (args: OnSelectArgs) => void
}

export const NmfcSearchContent = ({
  initialSearch,
  density,
  onSelect,
}: NmfcSearchContentProps) => {
  const [description, setDescription] = useState(initialSearch ?? '')
  const searchFormRef = useRef<SearchFormHandle>(null)
  const parsedNmfc = parseNmfc(description)
  const strDesc = !parsedNmfc.nmfc ? description : undefined
  const code = parsedNmfc.nmfc
  const descSearch = useNmfcDescriptionSearch({ description: strDesc })
  const articleSearch = useNmfcArticleSearch({ code: code?.toString() })

  return (
    <>
      <div className="flex whitespace-nowrap gap-x-2 sticky -mt-3 -top-6 z-10 bg-[#fafafa] py-6">
        <SearchForm
          className="mb-0"
          ref={searchFormRef}
          name="nmfc-search"
          placeholder="Commodity description or NMFC number"
          onChange={setDescription}
          isLoading={descSearch.isFetching}
          initialValue={description}
        />
        {density && (
          <div className="bg-fv-blue-50 flex items-center px-3 border-fv-gray border">
            Current density:&nbsp;<strong>{density}</strong>&nbsp;lbs. per cu ft
          </div>
        )}
      </div>
      {!code && (
        <SearchResults response={descSearch.data} onSelect={onSelect} />
      )}
      {code && articleSearch.data && (
        <ArticleDetails
          code={code}
          details={articleSearch.data}
          onSelect={onSelect}
          leftAligned
          onReferenceChange={({ code }) => {
            searchFormRef.current?.setValue(code)
          }}
        />
      )}
    </>
  )
}

type ResultsProps = {
  response: NmfcSearchResponse
  onSelect: (args: OnSelectArgs) => void
}
const SearchResults = ({ response, onSelect }: ResultsProps) => {
  const [expanded, setExpanded] = useState<number>()
  if (!response) return null
  if (!response.length) return <div>No results found</div>

  const groups = Object.entries(groupBy(response, 'grouping'))

  return (
    <div className="divide-y-[.35rem] divide-fv-gray-200 pb-8">
      {groups.map(([name, items]) => (
        <div key={name} className="py-5 first:pt-1">
          <NmfcGroupHeading name={name} />
          {items.map(item => (
            <Fragment key={item.nmfcNumber}>
              <div className="flex items-center peer [&.peer+&.peer]:mt-4 [&~div]:last:mb-0">
                <FvButton
                  icon="chevron-right"
                  theme="underlined"
                  className="flex-1 text-left"
                  iconClass={clsx('transition-transform', {
                    'rotate-90': expanded === item.nmfcNumber,
                  })}
                  onClick={() =>
                    setExpanded(p =>
                      p === item.nmfcNumber ? undefined : item.nmfcNumber,
                    )
                  }
                >
                  {item.description}
                </FvButton>
                <div className="h-0 border-t border-dashed border-fv-gray flex-1 mx-4" />
                <div className="">{item.nmfcNumber}</div>
              </div>

              {expanded === item.nmfcNumber && (
                <div className="mt-5">
                  <ArticleDetailsQuery
                    code={item.nmfcNumber.toString()}
                    onSelect={onSelect}
                  />
                </div>
              )}
            </Fragment>
          ))}
        </div>
      ))}
    </div>
  )
}
